import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useData } from "@/context/DataContext";
import { KpiCard, Panel, PageHeader } from "@/components/Widgets";
import { errMsg, http } from "@/lib/api";
import { IssueCard, NEEDS } from "@/pages/ReviewIssue";
import { PreviewTable } from "@/pages/ReviewPreview";

const defaults = (issues) => Object.fromEntries(issues.filter((i) => NEEDS.includes(i.type)).map((i) => {
  if (i.type === "unknown_sheet") return [i.id, { action: "ignore", name: i.sheet, category: "Corporates", ncf_label: "" }];
  if (i.type === "new_line") return [i.id, { action: "accept" }];
  if (i.type === "new_ncf_row") return [i.id, { action: "accept", role: i.role }];
  if (i.type === "new_loan") return [i.id, { action: "accept" }];
  return [i.id, { action: null }];
}));

export default function MappingReview() {
  const { id } = useParams();
  const nav = useNavigate();
  const { refresh, data } = useData();
  const entityNames = (data?.model?.entities || []).flatMap((e) => [e.name, e.short]);
  const [rv, setRv] = useState(null);
  const [dec, setDec] = useState({});
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    http.get(`/versions/${id}/review`).then((r) => { setRv(r.data); setDec(defaults(r.data.review.issues)); }).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  const issues = rv?.review.issues || [];
  const pendingAck = useMemo(() => issues.filter((i) => NEEDS.includes(i.type) && !dec[i.id]?.action).length, [issues, dec]);
  const dupNames = useMemo(() => issues.filter((i) => i.type === "unknown_sheet" && dec[i.id]?.action === "include"
    && entityNames.some((n) => n.toLowerCase() === (dec[i.id]?.name || "").trim().toLowerCase())).length, [issues, dec, entityNames]);
  if (!rv) return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" />Loading mapping preview…</div>;
  const s = rv.review.summary;
  const readOnly = rv.status === "confirmed";
  const confirm = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(dec).filter(([, v]) => v?.action));
      await http.post(`/versions/${id}/confirm`, { decisions: payload });
      await refresh();
      toast.success("Mapping confirmed — dashboard updated");
      nav("/");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };
  const missingSiblings = (sheet) => issues.filter((i) => i.type === "missing_item" && i.sheet === sheet).map((i) => i.label);
  return (
    <div data-testid="mapping-review-page">
      <PageHeader kicker={readOnly ? "Confirmed mapping" : "Step 2 of 2 · confirm before the dashboard updates"} title={`Mapping preview — ${rv.name}`}>
        {!readOnly && (
          <Button onClick={() => setOpen(true)} disabled={pendingAck > 0 || dupNames > 0} className="bg-amber-500 text-slate-950 hover:bg-amber-400" data-testid="mapping-confirm-button">
            Confirm & update dashboard
          </Button>
        )}
      </PageHeader>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <KpiCard testid="review-kpi-items" label="Items mapped" value={s.items} sub={`${s.entities} entities · ${s.years} FYs`} />
        <KpiCard testid="review-kpi-matched" label="Recognised" value={s.matched} tone="pos" />
        <KpiCard testid="review-kpi-new" label="New / unrecognised" value={s.new} tone={s.new ? "neg" : "pos"} />
        <KpiCard testid="review-kpi-new-sheet" label="In new sheets" value={s.new_sheet_items || 0} tone={s.new_sheet_items ? "amber" : "pos"} />
        <KpiCard testid="review-kpi-changed" label="Values changed" value={s.value_changed} tone="blue" />
        <KpiCard testid="review-kpi-missing" label="Missing" value={s.missing} tone={s.missing ? "neg" : "pos"} />
      </div>
      {!readOnly && (
        <Panel className="mt-6" title="Items requiring your confirmation" testid="review-issues-panel"
          subtitle={pendingAck ? `${pendingAck} item(s) still need acknowledgement` : "All items have a decision"}>
          {issues.length === 0 && <div className="text-sm text-emerald-400" data-testid="review-no-issues">Everything matches the confirmed mapping. Confirm to publish this version.</div>}
          <div className="space-y-3">
            {issues.map((i) => (
              <IssueCard key={i.id} issue={i} value={dec[i.id]} sections={rv.sections} aliasOptions={missingSiblings(i.sheet)} entityNames={entityNames}
                onChange={(v) => setDec((d) => ({ ...d, [i.id]: v }))} />
            ))}
          </div>
        </Panel>
      )}
      <PreviewTable preview={rv.review.preview} sections={rv.sections} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-slate-700 bg-[#131c2e]" data-testid="mapping-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Publish “{rv.name}”?</DialogTitle>
            <DialogDescription>
              The mapping rules will be updated with your decisions and this version becomes the active dashboard. Previous versions stay available.
            </DialogDescription>
          </DialogHeader>
          {issues.some((i) => i.type === "check_failed") && (
            <div className="flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-300">
              <AlertTriangle className="h-4 w-4 shrink-0" />Some reconciliation checks failed. Values will be shown as they appear in the workbook.
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="mapping-confirm-cancel">Cancel</Button>
            <Button onClick={confirm} disabled={busy} className="bg-amber-500 text-slate-950 hover:bg-amber-400" data-testid="mapping-confirm-submit">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
